import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import {
  LogOut,
  Moon,
  Sun,
  ChevronLeft,
  ChevronRight,
  Zap,
  X,
  Lock,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLogout, useUser } from '@/hooks/useAuth'
import { Button } from '@/components/ui'
import { useUiStore } from '@/stores/uiStore'
import { useIntegrationStatus } from '@/hooks/useIntegrations'
import { useConversations } from '@/hooks/useChat'
// Itens e ordem do menu moram em navItems.ts (r6-25) — uma unica fonte para desktop e mobile.
import { filterNavItemsByPlan, navItems } from './navItems'
import { hasFeature, showsLockedFeatures } from '@/lib/planFeatures'
import { LOCKED_FEATURE_COPY } from '@/lib/copy/lockedFeature'
import { LiveNowBadge } from '@/features/liveNow/LiveNowBadge'
import { STUDENT_LIVE_SESSIONS_PATH, useStudentLiveNow } from '@/features/liveNow/LiveNowNotices'
import { hasLiveNow } from '@/features/liveNow/liveNow'
import { UserAvatar } from './UserAvatar'

export function Sidebar() {
  const { sidebarCollapsed: collapsed, toggleSidebar, mobileSidebarOpen, setMobileSidebarOpen, resolvedTheme, toggleTheme } = useUiStore()
  const { user } = useUser()
  const { logout, isLoading } = useLogout()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: integrationStatus } = useIntegrationStatus()
  const { data: conversationsData } = useConversations()
  // R10-12: aula ao vivo agora -> selo vermelho em "Aulas ao vivo" (desktop e drawer mobile).
  const { data: liveNowSessions } = useStudentLiveNow()
  const isLiveNow = hasLiveNow(liveNowSessions)

  const hasAnyIntegration = integrationStatus?.integrations?.medway || integrationStatus?.integrations?.medcof

  // Calculate total unread messages
  const totalUnread = conversationsData?.conversations?.reduce(
    (sum, c) => sum + (c.unread_count || 0),
    0
  ) || 0

  // Filter nav items based on integration status
  // r7-25: primeiro o plano (feature fora do plano some, ou fica com cadeado se o plano
  // pedir), depois a integracao.
  const planNavItems = filterNavItemsByPlan(navItems, (feature) => hasFeature(user, feature), showsLockedFeatures(user))
  const filteredNavItems = planNavItems.filter(item => {
    if (!item.requiresIntegration) return true
    if (item.requiresIntegration === 'any') return hasAnyIntegration
    if (item.requiresIntegration === 'medway') return integrationStatus?.integrations?.medway
    if (item.requiresIntegration === 'medcof') return integrationStatus?.integrations?.medcof
    return true
  })

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false)
  }, [location.pathname, setMobileSidebarOpen])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <>
      {/* Mobile backdrop overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-screen transition-all duration-300 ease-out-expo',
          'bg-white dark:bg-neutral-800 border-r border-neutral-200 dark:border-neutral-700',
          // Desktop: normal sidebar behavior
          'lg:z-40',
          collapsed ? 'lg:w-20' : 'lg:w-64',
          // Mobile: full width drawer, hidden by default
          'w-72 -translate-x-full lg:translate-x-0',
          mobileSidebarOpen && 'translate-x-0'
        )}
      >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-neutral-200 dark:border-neutral-700">
          {/* Desktop: show logo based on collapsed state */}
          <div className="hidden lg:block">
            {collapsed ? (
              <img
                src="https://res.cloudinary.com/dbdotnzkl/image/upload/v1703301427/6628F599-739A-464B-8C06-CE45694EB634_n8bg0s.png"
                alt="MedHunter"
                className="h-10 w-10 object-contain"
              />
            ) : (
              <img
                src="https://res.cloudinary.com/dbdotnzkl/image/upload/v1702007903/Logotipo_Medhunter_1_awfuih.png"
                alt="MedHunter"
                className="h-8 object-contain dark:brightness-0 dark:invert"
              />
            )}
          </div>
          {/* Mobile: always show full logo */}
          <img
            src="https://res.cloudinary.com/dbdotnzkl/image/upload/v1702007903/Logotipo_Medhunter_1_awfuih.png"
            alt="MedHunter"
            className="h-8 object-contain dark:brightness-0 dark:invert lg:hidden"
          />
          {/* Desktop: collapse toggle */}
          <button
            onClick={toggleSidebar}
            className="hidden lg:block p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
          >
            {collapsed ? (
              <ChevronRight className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
            ) : (
              <ChevronLeft className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
            )}
          </button>
          {/* Mobile: close button */}
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
          >
            <X className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNavItems.map((item) => {
            // Add badge for Conversas
            const badge = item.path === '/app/social/conversations' ? totalUnread : 0
            // On mobile, always show full labels; on desktop, respect collapsed state
            const isCollapsedView = collapsed
            const showLiveBadge = isLiveNow && item.path === STUDENT_LIVE_SESSIONS_PATH
            // Fora do plano, com o plano pedindo "exibir bloqueado": cadeado no lugar do
            // icone, apagado e SEM link — nao e CTA, so mostra que existe.
            if (item.locked) {
              return (
                <div
                  key={item.path}
                  role="link"
                  aria-disabled="true"
                  aria-label={LOCKED_FEATURE_COPY.tabAriaLabel(item.label)}
                  title={LOCKED_FEATURE_COPY.tabTitle(item.label)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg relative select-none cursor-default opacity-40',
                    'text-neutral-600 dark:text-neutral-300',
                    isCollapsedView && 'lg:justify-center lg:px-2'
                  )}
                >
                  <Lock className="h-5 w-5 flex-shrink-0" aria-hidden />
                  <span className={cn('flex-1', isCollapsedView && 'lg:hidden')}>{item.label}</span>
                </div>
              )
            }
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 relative',
                    'hover:bg-primary-50 dark:hover:bg-neutral-700',
                    isActive
                      ? 'bg-primary-50 dark:bg-neutral-700 text-primary-600 dark:text-primary-400 font-medium'
                      : 'text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white',
                    // Only apply collapsed style on desktop
                    isCollapsedView && 'lg:justify-center lg:px-2'
                  )
                }
              >
                <div className="relative">
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {badge > 0 && isCollapsedView && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full hidden lg:block" />
                  )}
                  {showLiveBadge && isCollapsedView && (
                    <LiveNowBadge compact className="absolute -top-1 -right-1 hidden lg:flex" />
                  )}
                </div>
                {/* Mobile: always show labels; Desktop: hide when collapsed */}
                <span className={cn('flex-1', isCollapsedView && 'lg:hidden')}>{item.label}</span>
                {showLiveBadge && <LiveNowBadge className={cn(isCollapsedView && 'lg:hidden')} />}
                {badge > 0 && (
                  <span className={cn(
                    'ml-auto px-1.5 py-0.5 text-xs font-medium bg-red-500 text-white rounded-full min-w-[20px] text-center',
                    isCollapsedView && 'lg:hidden'
                  )}>
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </NavLink>
            )
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-neutral-200 dark:border-neutral-700">
          {/* Subscription Status - show on mobile always, desktop when not collapsed */}
          {user && (
            <div className={cn(
              'mb-4 p-3 rounded-lg bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/30 dark:to-secondary-900/30',
              collapsed && 'lg:hidden'
            )}>
              <div className="flex items-center gap-2 mb-1">
                <Zap className="h-4 w-4 text-primary-500 dark:text-primary-400" />
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                  {user.subscription_status === 'trial' ? 'Período de Teste' :
                   user.subscription_status === 'active' ? 'Plano Ativo' :
                   'Sem Plano'}
                </span>
              </div>
              {user.trial_info.in_trial && (
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {user.trial_info.days_remaining} dias restantes
                </p>
              )}
            </div>
          )}

          {/* User Info */}
          <div className={cn(
            'flex items-center gap-3 mb-3',
            collapsed && 'lg:justify-center'
          )}>
            <UserAvatar
              avatarUrl={user?.avatar_url}
              initial={user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
              className="h-10 w-10"
              fallbackClassName="bg-gradient-to-br from-primary-400 to-secondary-500 text-white font-medium"
            />
            {/* Mobile: always show; Desktop: hide when collapsed */}
            <div className={cn('overflow-hidden', collapsed && 'lg:hidden')}>
              <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                {user?.full_name || 'Usuário'}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                {user?.email}
              </p>
            </div>
          </div>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            onClick={toggleTheme}
            className={cn(
              'text-neutral-600 dark:text-neutral-300 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20',
              collapsed && 'lg:justify-center'
            )}
          >
            {/* O rotulo diz PARA ONDE o clique leva, e le o tema RESOLVIDO: quem esta em
                "seguir o sistema" com o computador no escuro tambem ve "Modo Claro". */}
            {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span className={cn(collapsed && 'lg:hidden')}>{resolvedTheme === 'dark' ? 'Modo Claro' : 'Modo Escuro'}</span>
          </Button>

          {/* Logout Button */}
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            onClick={handleLogout}
            isLoading={isLoading}
            className={cn(
              'text-neutral-600 dark:text-neutral-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20',
              collapsed && 'lg:justify-center'
            )}
          >
            <LogOut className="h-4 w-4" />
            <span className={cn(collapsed && 'lg:hidden')}>Sair</span>
          </Button>
        </div>
      </div>
    </aside>
    </>
  )
}
